import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '@/lib/api';

export const useMCPAPI = (baseUrl: string) => {
  const [servers, setServers] = useState([]);
  const [tools, setTools] = useState([]);
  const [resources, setResources] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoConnecting, setAutoConnecting] = useState(true);

  // Auto-connect on initialization
  useEffect(() => {
    const autoConnect = async () => {
      try {
        setAutoConnecting(true);
        console.log('🔌 Auto-connecting to MCP server...');
        
        // Check if user is authenticated first
        const isAuthenticated = await apiClient.checkAuth();
        if (!isAuthenticated) {
          throw new Error('User not authenticated');
        }
        
        // Try to connect to the default MCP server
        await connectServer('plusefin-mcp', '', []);
        console.log('✅ Auto-connection successful');
      } catch (error) {
        console.warn('⚠️ Auto-connection failed:', error.message);
        // Don't throw error - allow manual retry
      } finally {
        setAutoConnecting(false);
      }
    };

    autoConnect();
  }, []);

  // Load connected servers
  const loadServers = useCallback(async () => {
    try {
      const data = await apiClient.getPlaygroundServers();
      setServers(data || []);
      setConnected((data || []).length > 0);
      return data || [];
    } catch (error) {
      console.error('Failed to load servers:', error);
      throw error;
    }
  }, []);

  // Connect to a server
  const connectServer = useCallback(async (name: string, command: string = '', args: string[] = []) => {
    try {
      setLoading(true);
      const data = await apiClient.connectPlaygroundServer({ name, command, args });
      
      // Update server list
      await loadServers();
      setConnected(true);
      
      return data;
    } catch (error) {
      console.error('Failed to connect server:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadServers]);

  // Disconnect a server
  const disconnectServer = useCallback(async (name: string) => {
    try {
      setLoading(true);
      await apiClient.disconnectPlaygroundServer(name);
      
      await loadServers();
      setTools([]);
      setResources([]);
      setConnected(false);
    } catch (error) {
      console.error('Failed to disconnect server:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [loadServers]);

  // List tools from a server
  const listTools = useCallback(async (serverName: string) => {
    try {
      const data = await apiClient.getPlaygroundTools(serverName);
      setTools(data || []);
      return data || [];
    } catch (error) {
      console.error('Failed to list tools:', error);
      throw error;
    }
  }, []);

  // Call a tool
  const callTool = useCallback(async (serverName: string, toolName: string, args: any = {}) => {
    try {
      const data = await apiClient.callPlaygroundTool(serverName, toolName, args);
      return data;
    } catch (error) {
      console.error('Failed to call tool:', error);
      throw error;
    }
  }, []);

  // List resources from a server
  const listResources = useCallback(async (serverName: string) => {
    try {
      const data = await apiClient.getPlaygroundResources(serverName);
      setResources(data || []);
      return data || [];
    } catch (error) {
      console.error('Failed to list resources:', error);
      throw error;
    }
  }, []);

  // Get resource content
  const getResource = useCallback(async (serverName: string, uri: string) => {
    try {
      const data = await apiClient.getPlaygroundResource(serverName, uri);
      return data;
    } catch (error) {
      console.error('Failed to get resource:', error);
      throw error;
    }
  }, []);

  // Health check
  const healthCheck = useCallback(async () => {
    try {
      const data = await apiClient.getPlaygroundHealth();
      return data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }, []);

  // Retry connection
  const retryConnection = useCallback(async () => {
    setAutoConnecting(true);
    try {
      await connectServer('plusefin-mcp', '', []);
    } catch (error) {
      console.error('Retry connection failed:', error);
      throw error;
    } finally {
      setAutoConnecting(false);
    }
  }, [connectServer, listTools, listResources]);

  return {
    servers,
    tools,
    resources,
    connected,
    loading,
    autoConnecting,
    loadServers,
    connectServer,
    disconnectServer,
    listTools,
    callTool,
    listResources,
    getResource,
    healthCheck,
    retryConnection,
    setTools,
    setResources,
  };
};